import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, Loader2, Instagram } from "lucide-react";
import { toast } from "sonner";
import GlobeLines from "@/components/visual/GlobeLines";
import { FadeUp, Overline } from "@/components/visual/SectionHeading";
import SEO from "@/components/seo/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = { name: "", company: "", email: "", phone: "", message: "" };

const Contact = () => {
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Name, email, and message are required.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success("Submission received. Our team will respond within two working days.");
            setForm(initial);
        } catch (err) {
            const detail = err?.response?.data?.detail || "Unable to submit at this time. Please try again.";
            toast.error(typeof detail === "string" ? detail : "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="page-enter">
            <SEO
                title="Contact — Confidential Enquiries"
                description="Contact Indravax Strategic Advisory LLP for confidential strategic advisory engagements across defence, trade, energy, healthcare, and cross-border transactions. Office: New Delhi, India."
                path="/contact"
                keywords="contact indravax, strategic advisory contact, defence advisory contact, prateek grewal indravax, new delhi advisory"
            />
            <section className="relative min-h-[45vh] flex items-center overflow-hidden" data-testid="contact-hero">
                <GlobeLines className="opacity-15" />
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 w-full relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <Overline>Contact</Overline>
                        <h1 className="font-serif text-5xl md:text-7xl text-white leading-[1.02] mt-6 max-w-4xl">
                            Confidential. <span className="italic font-light text-[#C9A14A]">Direct.</span>
                        </h1>
                        <p className="mt-8 text-slate-400 max-w-xl text-lg font-light leading-relaxed">
                            Every submission is reviewed by a principal. We respond within two working days.
                        </p>
                    </motion.div>
                </div>
            </section>

            <section className="py-20 md:py-28 border-t border-[#C9A14A]/15" data-testid="contact-main">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
                    <FadeUp className="lg:col-span-5">
                        <Overline>Principal Office</Overline>
                        <h2 className="font-serif text-3xl md:text-4xl text-white mt-6 leading-[1.1]">
                            Indravax Strategic Advisory LLP
                        </h2>
                        <div className="mt-8 space-y-6 text-slate-300 font-light">
                            <div className="flex gap-4">
                                <MapPin size={18} className="text-[#C9A14A] mt-1 shrink-0" />
                                <div>
                                    17A/55-56, UG-6<br />
                                    Triveni Plaza, WEA<br />
                                    Karol Bagh, New Delhi – 110005<br />
                                    India
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <Phone size={18} className="text-[#C9A14A] mt-1 shrink-0" />
                                <a href="tel:+917814050036" className="hover:text-[#C9A14A] transition-colors">
                                    +91 78140 50036
                                </a>
                            </div>
                            <div className="flex gap-4">
                                <Mail size={18} className="text-[#C9A14A] mt-1 shrink-0" />
                                <a href="mailto:prateekgrewal@indravax.com" className="hover:text-[#C9A14A] transition-colors">
                                    prateekgrewal@indravax.com
                                </a>
                            </div>
                        </div>

                        <div className="mt-14 pt-10 border-t border-[#C9A14A]/15">
                            <Overline>Global Corridors</Overline>
                            <div className="mt-4 flex flex-wrap gap-3">
                                {["India", "Middle East", "Southeast Asia", "Europe", "Africa"].map((r) => (
                                    <span key={r} className="px-3 py-1.5 border border-[#C9A14A]/30 text-[0.7rem] tracking-[0.2em] uppercase text-slate-300">
                                        {r}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="mt-14 pt-10 border-t border-[#C9A14A]/15" data-testid="contact-connect">
                            <Overline>Connect With Us</Overline>
                            <p className="mt-5 text-sm text-slate-400 font-light leading-relaxed max-w-md">
                                Follow Indravax for updates, insights, and announcements.
                            </p>
                            <a
                                href="https://www.instagram.com/indravax"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-6 inline-flex items-center gap-3 group"
                                data-testid="contact-instagram-link"
                            >
                                <span className="inline-flex items-center justify-center w-11 h-11 border border-[#C9A14A]/40 text-[#C9A14A] group-hover:bg-[#C9A14A] group-hover:text-[#0A1F44] transition-colors duration-300">
                                    <Instagram size={18} strokeWidth={1.5} />
                                </span>
                                <span>
                                    <div className="text-sm text-white">Instagram</div>
                                    <div className="text-[0.65rem] tracking-[0.22em] uppercase text-[#C9A14A]">@indravax</div>
                                </span>
                            </a>
                        </div>

                        <p className="mt-14 text-xs text-slate-500 leading-relaxed font-light">
                            All communications are treated as confidential. Engagements are subject to KYC,
                            compliance screening, and applicable regulatory frameworks.
                        </p>
                    </FadeUp>

                    <FadeUp delay={0.15} className="lg:col-span-7">
                        <form
                            onSubmit={onSubmit}
                            className="institutional-card p-8 md:p-12"
                            data-testid="contact-form"
                        >
                            <Overline>Submit Enquiry</Overline>
                            <h3 className="font-serif text-3xl text-white mt-4 leading-tight">
                                Initiate a confidential conversation.
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8 mt-10">
                                <div className="md:col-span-1">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Name *</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={form.name}
                                        onChange={onChange}
                                        className="institutional-input"
                                        placeholder="Full name"
                                        required
                                        data-testid="contact-form-name"
                                    />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Company</label>
                                    <input
                                        type="text"
                                        name="company"
                                        value={form.company}
                                        onChange={onChange}
                                        className="institutional-input"
                                        placeholder="Organisation"
                                        data-testid="contact-form-company"
                                    />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Email *</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={form.email}
                                        onChange={onChange}
                                        className="institutional-input"
                                        placeholder="you@organisation.com"
                                        required
                                        data-testid="contact-form-email"
                                    />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Phone</label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={form.phone}
                                        onChange={onChange}
                                        className="institutional-input"
                                        placeholder="+..."
                                        data-testid="contact-form-phone"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Message *</label>
                                    <textarea
                                        name="message"
                                        value={form.message}
                                        onChange={onChange}
                                        className="institutional-input"
                                        placeholder="High-level description of the mandate or opportunity."
                                        rows={5}
                                        required
                                        data-testid="contact-form-message"
                                    />
                                </div>
                            </div>

                            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                                <p className="text-xs text-slate-500 max-w-sm leading-relaxed font-light">
                                    By submitting, you confirm the enquiry is made in good faith and in compliance
                                    with applicable laws.
                                </p>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                                    data-testid="contact-form-submit"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 size={14} className="animate-spin" /> Submitting
                                        </>
                                    ) : (
                                        <>
                                            Submit Enquiry <Send size={14} />
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </FadeUp>
                </div>
            </section>
        </div>
    );
};

export default Contact;
